import { BoxButton, Description, Divider, List, Svg, Table, TableCell } from '@repo/ui';
import { useRouter } from 'next/router';

import { ResultTableRow } from '@/components/tax-refund/apply/result/ResultTableRow';
import ApplyResultFaqView from '@/components/tax-refund/common/view/ApplyResultFaqView';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { getIndividualDefaultInfo } from '@/lib/constants/info-contents';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { ApplyResultData } from '@/lib/types/request';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyRefundResultNeedConfirmContent = ({ data }: { data: ApplyResultData }) => {
  const router = useRouter();
  const { hasRegisteredCard, cardName, cardLogo, cardNumber } = data.paymentCard;

  const faqContents = [
    {
      title: '환급금을 더 빨리 받고 싶어요.',
      content:
        '비즈넵 환급에서 경정청구 신고를 완료한 후 국세청 검토까지 완료되어야 최종 환급이 결정됩니다. 국세청 검토는 신고일로부터 평균적으로 2개월 가량 소요됩니다.\n편하게 확인하실 수 있도록 국세청 환급 결과 확인이 가능할 때 카카오톡 알림톡으로 안내해드리겠습니다.'
    },
    {
      title: '환급 신청한 지 2개월이 지났어요.',
      content: '국세청에서 추가 확인이 필요한 경우 2개월 이상 소요될 수 있습니다. 세부 진행사항이 궁금하시다면 고객센터로 문의해주세요.'
    },
    {
      title: '세금을 추가 납부하게 될 수도 있나요?',
      content:
        '비즈넵 환급에서는 제휴 세무사의 검토를 진행하고 있어 고객님께서 추가 납부하시는 일이 없도록 최대한 보수적으로 신고하고 있으니 안심하셔도 됩니다.'
    },
    {
      title: '환급받을 계좌를 변경하고 싶어요.',
      content: '신고가 완료되면 국세청에서 검토가 진행되기 때문에 계좌 변경이 어렵습니다. 국세 환급은 본인 명의의 계좌로만 지급이 가능한 점 참고해주세요.'
    },
    {
      title: '환급금을 어떻게 찾았나요?',
      content:
        '비즈넵 환급은 과거 5년간 개인사업자의 종합소득세와 개인의 자산 양도 시 발생한 양도소득세, 법인사업자에 대한 법인세를 신고하며 놓쳤던 세액 공제, 세액 감면 항목들을 고도화된 AI 기술로 찾아냅니다. 그리고 비즈넵 환급과 제휴한 세무 전문가가 탐색 결과값을 검토하여 놓치는 부분 없이 환급금을 찾아드리고 있습니다.'
    },
    {
      title: '농어촌 특별세 납부고지서가 왔는데 납부해야 하나요?',
      content:
        '농어촌 특별세 납부고지서를 받으셨다면 특정 조세 혜택이 잘 적용되어 환급 신청이 완료된 경우입니다.\n비즈넵 환급에서는 농어촌 특별세를 신고할 때 환급금에서 충당되도록 충당서를 함께 발송하기 때문에 따로 납부하지 않으셔도 됩니다.'
    }
  ];

  const infoContents = getIndividualDefaultInfo(hasRegisteredCard ?? false);

  const handleCardClick = () => {
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  return (
    <div>
      <div className={'px-4.5 py-normal-small'}>
        <Description
          title={`국세청 검토 결과,\n${data.name}님의 환급이 결정됐어요!`}
          size={'medium'}
          description={'결정된 환급액은 영업일 기준 2일 내에\n카카오 알림톡으로 안내드릴게요.'}
          graphic={<Svg src="icg_document-tax-check_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>종합소득세 예상 환급액</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{data.refundAmount}</div>
          <Divider className={'my-normal-medium'} />
          <ResultTableRow label={'환급 신청일'} value={data.applyDate} />
          <ResultTableRow label={'예상 환급일'} value={'신청일부터 2~3개월'} />
          <ResultTableRow label={'환급 계좌'} value={formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)} />
          <ResultTableRow label={'이용료'} value={`결정 환급액의 ${data.defaultFeeRate}%(vat별도)`} />
          {data.discountRate !== '0' ? (
            <Table
              headerCell={<TableCell variant={'header'}>이용료 할인</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className="mb-1">제휴 할인 {data.discountRate}% 적용</p>
                    <p className="text-13 text-gray-500">
                      결정 환급액의 <span className="line-through">{data.defaultFeeRate}%</span>-&gt;{data.feeRate}
                      %(vat별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          ) : (
            <></>
          )}
          {hasRegisteredCard && cardNumber && (
            <Table
              headerCell={
                <TableCell className={'flex items-center'} variant={'header'}>
                  결제 카드
                </TableCell>
              }
              contentCell={
                <>
                  <TableCell className={'flex items-center flex-1 justify-end'}>
                    <div className={'flex gap-tiny-medium text-[15px]'}>
                      <Svg src={cardLogo} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                  </TableCell>
                  <TableCell>
                    <BoxButton size={'small'} className={'ml-3'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </TableCell>
                </>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />

        <Divider />
        <div className={'pt-tiny-large'}>
          <List
            size={'small'}
            title={'환급이 결정되어 취소할 수 없어요.'}
            primaryAction
            state={'disabled'}
            supportingElement={
              <div className={`flex items-center opacity-40 body-small-medium`}>
                신청 취소
                <Svg src={'ic_chevron-right.svg'} width={18} height={18} />
              </div>
            }
          />
        </div>
      </div>
      <Divider size={'large'} />
      <ApplyResultFaqView data={faqContents} clickEventName={'indis-result_faq_result-check'} />
      <div className={'pb-normal-medium'} />
    </div>
  );
};

export default ApplyRefundResultNeedConfirmContent;
